// Resolve the scene manifest for a loose /ogs bundle, however it has to be
// obtained. This is the ONE place the two bundle layouts still differ.
//
// A multi_region bundle ships scene.json. A schema_version 1 bundle doesn't —
// but it IS a one-branch multi_region scene whose geom is fused, so we
// synthesize the equivalent manifest instead of forking the loader. Everything
// downstream then works off an identical shape.
//
// Split out of ogsContentPacks.js (which couples fetch + the Vite
// import.meta.env AAM base) so it stays unit-testable under plain `node --test`
// — same split as ogsContentSelect.js. `fetchFile` is injected for the same
// reason; the caller supplies the Cache-Storage-backed fetcher.

import { isMultiRegionScene, singleRegionScene } from '../ogs/assets/sceneManifest.ts'

const decode = (bytes) => new TextDecoder().decode(bytes)

/** Proxy URL for a named file in an /ogs list ({ filename, fileUrl } rows). */
export function bundleRowUrl(rows, name) {
  const r = (rows ?? []).find((x) => (x.filename ?? x.name) === name)
  return r ? (r.fileUrl ?? r.file_url) : null
}

function bundleRowSize(rows, name) {
  const r = (rows ?? []).find((x) => (x.filename ?? x.name) === name)
  return r ? (r.size ?? r.byte_size) : undefined
}

/**
 * @param slug      character slug, for error messages only
 * @param rows      the /ogs listing
 * @param files     LIVE VFS map; every file read here is seeded into it so the
 *                  caller's bulk fetch doesn't download it a second time
 * @param fetchFile async (url, label, size) => Uint8Array; throws on a bad response
 * @returns the raw scene manifest json (snake_case, as if it had been shipped)
 */
export async function resolveBundleManifest({ slug, rows, files, fetchFile }) {
  const url = (n) => bundleRowUrl(rows, n)
  const size = (n) => bundleRowSize(rows, n)

  const sceneUrl = url('scene.json')
  if (sceneUrl) {
    const bytes = await fetchFile(sceneUrl, 'scene.json', size('scene.json'))
    const json = JSON.parse(decode(bytes))
    if (!isMultiRegionScene(json)) {
      throw new Error(`/ogs bundle for '${slug}': scene.json is not a multi_region manifest`)
    }
    files.set('scene.json', bytes)
    return json
  }

  // schema_version 1. Everything the synthesized manifest needs comes from the
  // bundle itself — uv_res from mesh.json, the control count from
  // rig_names.json. Scale/offset/plücker conditioning are deliberately NOT
  // supplied: they route through parseSceneManifest's defaults, the same ones a
  // multi_region bundle gets (see singleRegionScene).
  const meshUrl = url('mesh.json')
  if (!meshUrl) {
    throw new Error(`/ogs bundle for '${slug}' has neither scene.json nor mesh.json — not an OGS bundle`)
  }
  const meshBytes = await fetchFile(meshUrl, 'mesh.json', size('mesh.json'))
  files.set('mesh.json', meshBytes)
  const meshMeta = JSON.parse(decode(meshBytes))

  // rig_names.json is REQUIRED here: it is the only statement of this bundle's
  // control-space width. Guessing a dimension would decode garbage (and the
  // gather that drives the face is derived from these names anyway), so a bundle
  // without it fails loudly rather than rendering something wrong.
  const rigNamesUrl = url('rig_names.json')
  if (!rigNamesUrl) {
    throw new Error(`/ogs bundle for '${slug}' has no rig_names.json — cannot determine the rig dimension`)
  }
  const rigNamesBytes = await fetchFile(rigNamesUrl, 'rig_names.json', size('rig_names.json'))
  files.set('rig_names.json', rigNamesBytes)
  const rigNames = JSON.parse(decode(rigNamesBytes))

  const json = singleRegionScene({
    subject: slug,
    uvRes: Number(meshMeta.uv_res),
    rigDim: rigNames.length,
    hasRig2Mesh: !!url('rig2mesh.onnx'),
  })
  // Seed the synthesized manifest under its canonical name so multiRegionFileList
  // finds it already resident and never tries to download a file that, for this
  // bundle layout, does not exist on the server.
  files.set('scene.json', new TextEncoder().encode(JSON.stringify(json)))
  return json
}
